/**
 * @file DocumentItemsTable.tsx
 * @description Таблица позиций документа на `≥lg` (phase19.24 этап 1, шаг 1;
 * admin-design-system.md §5.1, блок (4)).
 *
 * Один рецепт строки на приёмку, списание, перемещение и сборку — до него
 * четыре документа рисовали позиции четырьмя разными разметками (две
 * `<table>`, одна сетка на `<li>` и карточка, растянутая на 1280px).
 *
 * Колонки приходят ДАННЫМИ: `{ key, label, width?, align?, render }`. Новая
 * колонка документа = запись в массиве, а не ветка в разметке (OCP). Каркас
 * жёсткий: первая колонка — фото 80px, вторая — «Товар / артикул», последняя —
 * «⋯» 72px. Между ними стоят колонки документа: «Партия · до» и «Ячейка» у
 * приёмки, «Причина» у списания, «Откуда → Куда» у перемещения, «Откуда
 * брать» и «Собрано / план» у сборки.
 *
 * Ширины посчитаны под 1280px: там левой колонке достаётся 544px (сайдбар 240
 * + поля страницы + панель 360). Поэтому каркас занимает 80 + 72 = 152px,
 * «Товар / артикул» — 30% (≈163px), а на колонки документа остаётся ≈229px:
 * числовые делаем 72–88px, текстовые отдают остаток. Ячейки — `px-3`: `px-4`
 * съедал ещё 48px и выталкивал «Кол-во» за край.
 *
 * `minWidth` — нижняя граница таблицы (по умолчанию 540px). Меньше неё
 * колонки не ужимаются: обёртка получает `overflow-x-auto` и скроллится
 * ТОЛЬКО рамка таблицы, а не страница. Так документ с живыми контролами в
 * строке (перемещение) остаётся рабочим на 1024px, где панель забирает 320px.
 *
 * `table-fixed` + `colgroup`: без них длинное название товара растягивало
 * колонку и уводило «Кол-во» за край.
 *
 * Блок лежит на непрозрачной поверхности темы (SC69, phase19.31): строки не
 * пропускают декоративный фон кабинета; выбранная строка — токен темы.
 *
 * На `<lg` таблицы нет: там `DocumentCard` — вызывающий рисует карточки сам.
 */

import type { ReactNode } from "react";

import { LIST_SURFACE_CLASS, ROW_SURFACE_TINT } from "./ListStates";
import { cn } from "@/lib/utils";

/** Колонка документа между «Товар / артикул» и «⋯». */
export interface DocumentTableColumn<T> {
  key: string;
  /** Подпись колонки капсом. */
  label: string;
  /** Ширина колонки: `96px`, `20%`. Без неё колонка делит остаток поровну. */
  width?: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

/** Состояние строки — фоном и ring, как у `DocumentCard`. */
export type DocumentRowTone = "default" | "selected" | "done" | "danger";

/**
 * Реестр состояний строки: новый тон = запись здесь. Подложки — непрозрачные
 * смеси с поверхностью темы (SC69), наведение — токен строки таблицы.
 */
const ROW_TONE: Record<DocumentRowTone, string> = {
  default: "hover:bg-[var(--row-hover)]",
  selected: ROW_SURFACE_TINT.selected,
  done: "text-on-surface-variant hover:bg-[var(--row-hover)]",
  danger: cn(ROW_SURFACE_TINT.danger, "ring-1 ring-destructive/40"),
};

interface Props<T> {
  items: T[];
  rowKey: (row: T) => string;
  /** `id` строки для прокрутки к отсканированной позиции. */
  rowId?: (row: T) => string;
  columns: DocumentTableColumn<T>[];
  /** Фото фасовки 56px или иконка-заглушка. */
  media: (row: T) => ReactNode;
  /** Название позиции с фасовкой — первая строка второй колонки. */
  title: (row: T) => ReactNode;
  /** Артикул моноширинным — вторая строка. */
  code?: (row: T) => ReactNode;
  /** Третья строка: цены приёмки, причина недостачи. */
  note?: (row: T) => ReactNode;
  /** Меню «⋯» строки (`RowActionsMenu`). */
  actions?: (row: T) => ReactNode;
  rowTone?: (row: T) => DocumentRowTone;
  /** Минимальная ширина таблицы; уже неё скроллится рамка. По умолчанию 540px. */
  minWidth?: string;
  /** Текст вместо таблицы, когда позиций нет. Без него пустой блок не рисуем. */
  emptyText?: string;
  className?: string;
  /**
   * С какой ширины таблица видна (phase19.31): `lg` (по умолчанию) или `xl`.
   * `xl` — документ с боковой панелью: на 1024 колонке позиций остаётся
   * ~370px, таблица в неё не помещалась и прокручивалась, «Ячейка» и
   * «Кол-во» были не видны (дефект приёмки 2026-10-02). Вызывающий тогда
   * показывает карточки до 1280 (`xl:hidden`).
   */
  breakpoint?: "lg" | "xl";
  /** Без колонки фото 80px (заглушка-значок места не стоит, phase19.31). */
  hideMedia?: boolean;
}

/** Видимость таблицы по порогу — реестром, без сборки классов на лету. */
const SHELL_VISIBLE: Record<"lg" | "xl", string> = {
  lg: "hidden lg:block",
  xl: "hidden xl:block",
};

export function DocumentItemsTable<T>({
  items,
  rowKey,
  rowId,
  columns,
  media,
  title,
  code,
  note,
  actions,
  rowTone,
  minWidth = "540px",
  emptyText,
  className,
  breakpoint = "lg",
  hideMedia = false,
}: Props<T>) {
  // Непрозрачная поверхность блока (SC69): фон кабинета не просвечивает.
  const shell = cn(SHELL_VISIBLE[breakpoint], "rounded-xl ring-ghost", LIST_SURFACE_CLASS, className);

  if (items.length === 0) {
    if (!emptyText) return null;
    return (
      <div className={shell}>
        <p className="px-4 py-6 text-sm text-on-surface-variant">{emptyText}</p>
      </div>
    );
  }

  return (
    // overflow-x-auto, а не hidden: на 1024px панель забирает 320px, и узкую
    // таблицу лучше проскроллить внутри рамки, чем обрезать колонку «Кол-во».
    <div className={cn(shell, "overflow-x-auto")}>
      <table className="w-full table-fixed text-sm" style={{ minWidth }}>
        <colgroup>
          {hideMedia ? null : <col className="w-[80px]" />}
          <col className="w-[30%]" />
          {columns.map((column) => (
            <col
              key={column.key}
              style={column.width ? { width: column.width } : undefined}
            />
          ))}
          <col className="w-[72px]" />
        </colgroup>
        <thead className="bg-on-surface/5 text-left">
          <tr className="label-editorial">
            {/* relative: у `sr-only` внутри `th` позиционирование считается от
                документа и тянет горизонтальный скролл страницы (находка QA). */}
            {hideMedia ? null : (
              <th className="relative px-3 py-3">
                <span className="sr-only">Фото</span>
              </th>
            )}
            <th className="px-3 py-3">Товар / артикул</th>
            {columns.map((column) => (
              <th
                key={column.key}
                className={cn("px-3 py-3", column.align === "right" && "text-right")}
              >
                {column.label}
              </th>
            ))}
            <th className="relative px-3 py-3">
              <span className="sr-only">Действия</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={rowKey(row)}
              id={rowId?.(row)}
              data-row-id={rowId?.(row)}
              className={cn(
                "border-t border-on-surface/8 align-top transition-colors",
                ROW_TONE[rowTone?.(row) ?? "default"],
              )}
            >
              {hideMedia ? null : <td className="px-3 py-3">{media(row)}</td>}
              <td className="px-3 py-3">
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium">{title(row)}</span>
                  {code ? (
                    <span className="truncate font-mono text-xs text-on-surface-variant">
                      {code(row)}
                    </span>
                  ) : null}
                  {note?.(row)}
                </span>
              </td>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn("px-3 py-3", column.align === "right" && "text-right")}
                >
                  {column.render(row)}
                </td>
              ))}
              <td className="px-3 py-3">
                <div className="flex justify-end">{actions?.(row)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
